/**
 * FirebaseAppointmentService - Cloud-based appointment and scheduling management
 *
 * Features:
 * - Real-time appointment syncing across devices
 * - Provider availability management in Firestore
 * - Calendar integration with cloud storage
 * - Multi-user video session support
 * - Automatic conflict detection
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  writeBatch,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { AuditService } from './AuditService';

export type AppointmentModality = 'video' | 'in-person';
export type AppointmentStatus = 'scheduled' | 'confirmed' | 'in-progress' | 'completed' | 'cancelled' | 'no-show';
export type SessionType = 'initial-consultation' | 'follow-up' | 'therapy' | 'assessment' | 'couples' | 'other';

export interface Appointment {
  id: string;
  patientId: string;
  patientName?: string;
  providerId: string;
  providerName?: string;

  // Couples session support
  coupleId?: string;
  patient2Id?: string;
  patient2Name?: string;
  appointmentDate: Date;
  startTime: string;
  endTime: string;
  duration: number;
  modality: AppointmentModality;
  sessionType: SessionType;
  status: AppointmentStatus;

  // Modality-specific
  videoLink?: string;
  meetingRoomId?: string;
  location?: {
    latitude: number;
    longitude: number;
    timestamp: string;
    accuracy?: number;
  } | string;

  // Additional info
  appointmentReason?: string;
  notes?: string;

  // Reminders
  reminderSent?: boolean;
  reminder24h?: boolean;
  reminder1h?: boolean;

  // Tracking
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;

  // Session link
  sessionId?: string;
}

export type AppointmentTypeKey = 'initial-consultation' | 'therapy' | 'check-in' | 'couples' | 'couples-initial';

export interface AvailabilityBlock {
  startTime: string;
  endTime: string;
  slotDuration: number;
  modalities: AppointmentModality[];
  appointmentTypes: AppointmentTypeKey[];
}

export interface ProviderAvailability {
  id: string;
  providerId: string;
  dayOfWeek: number;
  isActive: boolean;

  // Multi-block scheduling (new)
  blocks?: AvailabilityBlock[];

  // Legacy flat fields (backward compat — used if blocks is empty/undefined)
  startTime: string;
  endTime: string;
  modalities: AppointmentModality[];
  slotDuration: number;
  maxPatientsPerSlot?: number;
  location?: string;
  appointmentTypes?: AppointmentTypeKey[];

  breakTimes?: Array<{
    startTime: string;
    endTime: string;
    reason?: string;
  }>;
}

export interface DateSpecificAvailability {
  id: string;
  providerId: string;
  date: string;
  isAvailable: boolean;

  startTime?: string;
  endTime?: string;
  modalities?: AppointmentModality[];
  slotDuration?: number;
  location?: string;
  reason?: string;

  breakTimes?: Array<{
    startTime: string;
    endTime: string;
    reason?: string;
  }>;
}

export interface TimeSlot {
  startTime: string;
  endTime: string;
  available: boolean;
  appointmentId?: string;
  modalities: AppointmentModality[];
}

export interface AppointmentFilters {
  patientId?: string;
  providerId?: string;
  modality?: AppointmentModality;
  status?: AppointmentStatus;
  startDate?: Date;
  endDate?: Date;
  sessionType?: SessionType;
  includeCancelled?: boolean;
}

class FirebaseAppointmentService {
  private static instance: FirebaseAppointmentService;
  private appointmentsCollection = 'appointments';
  private availabilityCollection = 'provider_availability';
  private dateSpecificCollection = 'date_specific_availability';

  private constructor() {}

  static getInstance(): FirebaseAppointmentService {
    if (!FirebaseAppointmentService.instance) {
      FirebaseAppointmentService.instance = new FirebaseAppointmentService();
    }
    return FirebaseAppointmentService.instance;
  }

  // ==================== Appointment Management ====================

  /**
   * Create a new appointment
   */
  async createAppointment(
    appointment: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt' | 'videoLink' | 'meetingRoomId'>
  ): Promise<Appointment> {
    try {
      const now = new Date();

      const appointmentData: Record<string, any> = {
        ...appointment,
        appointmentDate: Timestamp.fromDate(new Date(appointment.appointmentDate)),
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
        status: appointment.status || 'scheduled'
      };

      // Add video fields only for video appointments
      if (appointment.modality === 'video') {
        appointmentData.videoLink = this.generateVideoLink('temp');
        appointmentData.meetingRoomId = this.generateMeetingRoomId();
      }

      // Strip any undefined values — Firestore rejects them
      Object.keys(appointmentData).forEach(key => {
        if (appointmentData[key] === undefined) {
          delete appointmentData[key];
        }
      });

      const docRef = await addDoc(collection(db, this.appointmentsCollection), appointmentData);

      // Update video link with actual ID
      if (appointmentData.videoLink) {
        const actualVideoLink = this.generateVideoLink(docRef.id);
        await updateDoc(docRef, { videoLink: actualVideoLink });
      }

      const newAppointment: Appointment = {
        ...appointment,
        id: docRef.id,
        createdAt: now,
        updatedAt: now,
        videoLink: appointmentData.videoLink ? this.generateVideoLink(docRef.id) : undefined,
        meetingRoomId: appointmentData.meetingRoomId,
        status: appointment.status || 'scheduled'
      };

      AuditService.logPhiModification(
        (appointment as any).createdBy || appointment.patientId || appointment.providerId,
        'provider', 'create', 'appointment', docRef.id,
        appointment.patientId, `Appointment created: ${appointment.modality} ${appointment.sessionType}`
      );
      return newAppointment;
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  }

  /**
   * Update an existing appointment
   */
  async updateAppointment(id: string, updates: Partial<Appointment>): Promise<Appointment | null> {
    try {
      const docRef = doc(db, this.appointmentsCollection, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        console.error(`Appointment not found: ${id}`);
        return null;
      }

      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date())
      };

      // Convert Date fields to Timestamps
      if (updates.appointmentDate) {
        updateData.appointmentDate = Timestamp.fromDate(new Date(updates.appointmentDate));
      }

      // Remove id from updates
      delete updateData.id;
      delete updateData.createdAt;

      await updateDoc(docRef, updateData);

      // Get updated document
      const updatedSnap = await getDoc(docRef);
      const existingData = docSnap.data();
      AuditService.logPhiModification(
        existingData.providerId, 'provider', 'update', 'appointment', id,
        existingData.patientId, `Appointment updated: ${Object.keys(updates).join(', ')}`
      );
      return this.firestoreToAppointment({ id: updatedSnap.id, ...updatedSnap.data() });
    } catch (error) {
      console.error('Error updating appointment:', error);
      throw error;
    }
  }

  /**
   * Cancel an appointment
   */
  async cancelAppointment(id: string, reason?: string): Promise<boolean> {
    try {
      const docRef = doc(db, this.appointmentsCollection, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return false;
      }

      const appointment = docSnap.data();
      const cancelNote = `Cancelled: ${reason || 'No reason provided'}`;
      const updatedNotes = appointment.notes
        ? `${appointment.notes}\n${cancelNote}`
        : cancelNote;

      await updateDoc(docRef, {
        status: 'cancelled',
        notes: updatedNotes,
        updatedAt: Timestamp.fromDate(new Date())
      });

      AuditService.logPhiModification(
        appointment.providerId, 'provider', 'update', 'appointment', id,
        appointment.patientId, `Appointment cancelled: ${reason || 'No reason provided'}`
      );
      return true;
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      throw error;
    }
  }

  /**
   * Mark appointment as in-progress (session started)
   */
  async startAppointment(id: string, sessionId: string): Promise<Appointment | null> {
    try {
      const docRef = doc(db, this.appointmentsCollection, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      await updateDoc(docRef, {
        status: 'in-progress',
        sessionId,
        updatedAt: Timestamp.fromDate(new Date())
      });

      const updatedSnap = await getDoc(docRef);
      return this.firestoreToAppointment({ id: updatedSnap.id, ...updatedSnap.data() });
    } catch (error) {
      console.error('Error starting appointment:', error);
      throw error;
    }
  }

  /**
   * Mark appointment as completed
   */
  async completeAppointment(id: string): Promise<Appointment | null> {
    try {
      const docRef = doc(db, this.appointmentsCollection, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      await updateDoc(docRef, {
        status: 'completed',
        updatedAt: Timestamp.fromDate(new Date())
      });

      const updatedSnap = await getDoc(docRef);
      return this.firestoreToAppointment({ id: updatedSnap.id, ...updatedSnap.data() });
    } catch (error) {
      console.error('Error completing appointment:', error);
      throw error;
    }
  }

  /**
   * Get appointment by ID
   */
  async getAppointment(id: string): Promise<Appointment | null> {
    try {
      const docRef = doc(db, this.appointmentsCollection, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.firestoreToAppointment({ id: docSnap.id, ...docSnap.data() });
    } catch (error) {
      console.error('Error getting appointment:', error);
      throw error;
    }
  }

  /**
   * Get all appointments with optional filters
   */
  async getAppointments(filters?: AppointmentFilters): Promise<Appointment[]> {
    try {
      const constraints: QueryConstraint[] = [];

      // Build query constraints
      if (filters?.patientId) {
        constraints.push(where('patientId', '==', filters.patientId));
      }
      if (filters?.providerId) {
        constraints.push(where('providerId', '==', filters.providerId));
      }
      if (filters?.modality) {
        constraints.push(where('modality', '==', filters.modality));
      }
      if (filters?.status) {
        constraints.push(where('status', '==', filters.status));
      }
      if (filters?.sessionType) {
        constraints.push(where('sessionType', '==', filters.sessionType));
      }

      // Date range filters
      if (filters?.startDate) {
        constraints.push(where('appointmentDate', '>=', Timestamp.fromDate(filters.startDate)));
      }
      if (filters?.endDate) {
        constraints.push(where('appointmentDate', '<=', Timestamp.fromDate(filters.endDate)));
      }

      // Order by appointment date
      constraints.push(orderBy('appointmentDate', 'asc'));

      const q = query(collection(db, this.appointmentsCollection), ...constraints);
      const querySnapshot = await getDocs(q);

      let appointments = querySnapshot.docs.map(doc =>
        this.firestoreToAppointment({ id: doc.id, ...doc.data() })
      );

      // Filter out cancelled/no-show unless explicitly included
      if (!filters?.includeCancelled) {
        appointments = appointments.filter(
          a => a.status !== 'cancelled' && a.status !== 'no-show'
        );
      }

      return appointments;
    } catch (error) {
      console.error('Error getting appointments:', error);
      throw error;
    }
  }

  /**
   * Get all appointments for a patient, including couples appointments where they are patient2
   */
  async getPatientAppointmentsIncludingCouples(patientId: string): Promise<Appointment[]> {
    try {
      // Get appointments where patient is primary
      const primary = await this.getAppointments({ patientId });

      // Get appointments where patient is patient2 (couples)
      const q = query(
        collection(db, this.appointmentsCollection),
        where('patient2Id', '==', patientId),
        orderBy('appointmentDate', 'asc')
      );
      const snap = await getDocs(q);
      const coupleAppts = snap.docs.map(d => this.firestoreToAppointment({ id: d.id, ...d.data() }))
        .filter(a => a.status !== 'cancelled' && a.status !== 'no-show');

      // Merge and deduplicate by id
      const allMap = new Map<string, Appointment>();
      [...primary, ...coupleAppts].forEach(a => allMap.set(a.id, a));
      return Array.from(allMap.values()).sort((a, b) =>
        new Date(a.appointmentDate).getTime() - new Date(b.appointmentDate).getTime()
      );
    } catch (error) {
      console.error('Error getting patient appointments including couples:', error);
      // Fallback to primary only
      return this.getAppointments({ patientId });
    }
  }

  /**
   * Get upcoming appointments for a patient
   */
  async getUpcomingAppointments(patientId: string, limit: number = 5, providerId?: string): Promise<Appointment[]> {
    const now = new Date();
    const appointments = await this.getAppointments({
      patientId,
      providerId,
      startDate: now
    });

    return appointments
      .filter(a => a.status === 'scheduled' || a.status === 'confirmed')
      .slice(0, limit);
  }

  /**
   * Get today's appointments for a provider
   */
  async getTodaysAppointments(providerId: string): Promise<Appointment[]> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return this.getAppointments({
      providerId,
      startDate: today,
      endDate: tomorrow
    });
  }

  /**
   * Get appointments for a specific date range (for calendar view)
   */
  async getAppointmentsByDateRange(
    providerId: string,
    startDate: Date,
    endDate: Date
  ): Promise<Appointment[]> {
    return this.getAppointments({
      providerId,
      startDate,
      endDate
    });
  }

  /**
   * Check if appointment can be joined (for video appointments)
   */
  async canJoinAppointment(
    appointmentId: string
  ): Promise<{ canJoin: boolean; reason?: string; minutesUntil?: number }> {
    try {
      const appointment = await this.getAppointment(appointmentId);

      if (!appointment) {
        return { canJoin: false, reason: 'Appointment not found' };
      }

      if (appointment.modality !== 'video') {
        return { canJoin: false, reason: 'Not a video appointment' };
      }

      if (appointment.status === 'cancelled') {
        return { canJoin: false, reason: 'Appointment is cancelled' };
      }

      // Allow joining 15 minutes early
      const now = new Date();
      const appointmentDateTime = this.getAppointmentDateTime(appointment);
      const fifteenMinutesBefore = new Date(appointmentDateTime.getTime() - 15 * 60 * 1000);
      const thirtyMinutesAfter = new Date(appointmentDateTime.getTime() + 30 * 60 * 1000);

      // If completed, allow rejoining within 2 hours of scheduled start (reconnect after drop)
      if (appointment.status === 'completed') {
        const twoHoursAfter = new Date(appointmentDateTime.getTime() + 2 * 60 * 60 * 1000);
        if (now <= twoHoursAfter) {
          return { canJoin: true };
        }
        return { canJoin: false, reason: 'Appointment is already completed' };
      }

      console.log('canJoinAppointment debug:', {
        now: now.toString(),
        appointmentDateTime: appointmentDateTime.toString(),
        fifteenMinutesBefore: fifteenMinutesBefore.toString(),
        thirtyMinutesAfter: thirtyMinutesAfter.toString(),
        startTime: appointment.startTime,
        appointmentDate: appointment.appointmentDate?.toString(),
      });

      if (now < fifteenMinutesBefore) {
        const minutesUntil = Math.floor(
          (fifteenMinutesBefore.getTime() - now.getTime()) / (60 * 1000)
        );
        return { canJoin: false, reason: 'Too early to join', minutesUntil };
      }

      if (now > thirtyMinutesAfter) {
        return { canJoin: false, reason: 'Appointment window has passed' };
      }

      return { canJoin: true };
    } catch (error) {
      console.error('Error checking if appointment can be joined:', error);
      return { canJoin: false, reason: 'Error checking appointment' };
    }
  }

  // ==================== Provider Availability ====================

  /**
   * Set provider availability for a day of the week
   */
  async setProviderAvailability(
    availability: Omit<ProviderAvailability, 'id'>
  ): Promise<ProviderAvailability> {
    try {
      // Use deterministic doc ID so no query needed (avoids composite index requirement)
      const docId = `${availability.providerId}_${availability.dayOfWeek}`;
      const docRef = doc(db, this.availabilityCollection, docId);
      const data = {
        ...availability,
        isActive: availability.isActive !== undefined ? availability.isActive : true
      };

      await setDoc(docRef, data);

      return { id: docId, ...data };
    } catch (error) {
      console.error('Error setting provider availability:', error);
      throw error;
    }
  }

  /**
   * Get provider availability for all days
   */
  async getProviderAvailability(providerId: string): Promise<ProviderAvailability[]> {
    try {
      const q = query(
        collection(db, this.availabilityCollection),
        where('providerId', '==', providerId)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ProviderAvailability));
    } catch (error) {
      console.error('Error getting provider availability:', error);
      throw error;
    }
  }

  // ==================== Date-Specific Availability ====================

  /**
   * Set date-specific availability override
   */
  async setDateSpecificAvailability(
    availability: Omit<DateSpecificAvailability, 'id'>
  ): Promise<DateSpecificAvailability> {
    try {
      // Check if override already exists for this provider + date
      const existingQuery = query(
        collection(db, this.dateSpecificCollection),
        where('providerId', '==', availability.providerId),
        where('date', '==', availability.date)
      );

      const existingDocs = await getDocs(existingQuery);

      // If exists, update it; otherwise create new
      if (!existingDocs.empty) {
        const existingDoc = existingDocs.docs[0];
        await updateDoc(existingDoc.ref, availability);

        console.log(`Date-specific availability updated for ${availability.providerId} on ${availability.date}`);

        return {
          id: existingDoc.id,
          ...availability
        };
      } else {
        const docRef = await addDoc(collection(db, this.dateSpecificCollection), availability);

        console.log(`Date-specific availability created for ${availability.providerId} on ${availability.date}`);

        return {
          id: docRef.id,
          ...availability
        };
      }
    } catch (error) {
      console.error('Error setting date-specific availability:', error);
      throw error;
    }
  }

  /**
   * Get date-specific availability override for a specific date
   */
  async getDateSpecificAvailability(
    providerId: string,
    date: Date
  ): Promise<DateSpecificAvailability | null> {
    try {
      const dateStr = date.toISOString().split('T')[0];
      const q = query(
        collection(db, this.dateSpecificCollection),
        where('providerId', '==', providerId),
        where('date', '==', dateStr)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      } as DateSpecificAvailability;
    } catch (error) {
      console.error('Error getting date-specific availability:', error);
      throw error;
    }
  }

  /**
   * Get all date-specific availability overrides for a provider
   */
  async getAllDateSpecificAvailability(providerId: string): Promise<DateSpecificAvailability[]> {
    try {
      const q = query(
        collection(db, this.dateSpecificCollection),
        where('providerId', '==', providerId)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as DateSpecificAvailability));
    } catch (error) {
      console.error('Error getting all date-specific availability:', error);
      throw error;
    }
  }

  /**
   * Delete a date-specific availability override
   */
  async deleteDateSpecificAvailability(providerId: string, date: Date): Promise<boolean> {
    try {
      const dateStr = date.toISOString().split('T')[0];
      const q = query(
        collection(db, this.dateSpecificCollection),
        where('providerId', '==', providerId),
        where('date', '==', dateStr)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return false;
      }

      const batch = writeBatch(db);
      querySnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error deleting date-specific availability:', error);
      throw error;
    }
  }

  /**
   * Get available time slots for a specific date
   */
  async getAvailableSlots(providerId: string, date: Date): Promise<TimeSlot[]> {
    try {
      // Check for date-specific override first
      const dateOverride = await this.getDateSpecificAvailability(providerId, date);

      if (dateOverride) {
        if (!dateOverride.isAvailable) {
          return [];
        }

        // Generate slots from date-specific availability
        return this.generateSlots(
          providerId,
          date,
          dateOverride.startTime!,
          dateOverride.endTime!,
          dateOverride.slotDuration || 60,
          dateOverride.modalities || ['video', 'in-person'],
          dateOverride.breakTimes
        );
      }

      // No date-specific override - use weekly recurring availability
      const dayOfWeek = date.getDay();
      const availabilities = await this.getProviderAvailability(providerId);
      const dayAvailability = availabilities.find(a => a.dayOfWeek === dayOfWeek && a.isActive);

      if (!dayAvailability) {
        return [];
      }

      // Multi-block support: generate slots from each block
      if (dayAvailability.blocks && dayAvailability.blocks.length > 0) {
        const allSlots: any[] = [];
        for (const block of dayAvailability.blocks) {
          const blockSlots = await this.generateSlots(
            providerId,
            date,
            block.startTime,
            block.endTime,
            block.slotDuration,
            block.modalities,
            undefined
          );
          // Tag each slot with the block's appointment types
          blockSlots.forEach(s => (s as any).appointmentTypes = block.appointmentTypes);
          allSlots.push(...blockSlots);
        }
        return allSlots;
      }

      // Legacy single-block fallback
      return this.generateSlots(
        providerId,
        date,
        dayAvailability.startTime,
        dayAvailability.endTime,
        dayAvailability.slotDuration,
        dayAvailability.modalities,
        dayAvailability.breakTimes
      );
    } catch (error) {
      console.error('Error getting available slots:', error);
      throw error;
    }
  }

  /**
   * Check if a time slot is available
   */
  async isSlotAvailable(
    providerId: string,
    date: Date,
    startTime: string,
    duration: number
  ): Promise<boolean> {
    const slots = await this.getAvailableSlots(providerId, date);
    const slot = slots.find(s => s.startTime === startTime);
    return slot ? slot.available : false;
  }

  // ==================== Real-time Subscriptions ====================

  /**
   * Subscribe to appointment changes
   */
  subscribeToAppointments(
    filters: AppointmentFilters,
    callback: (appointments: Appointment[]) => void
  ): Unsubscribe {
    try {
      const constraints: QueryConstraint[] = [];

      if (filters.patientId) {
        constraints.push(where('patientId', '==', filters.patientId));
      }
      if (filters.providerId) {
        constraints.push(where('providerId', '==', filters.providerId));
      }
      if (filters.modality) {
        constraints.push(where('modality', '==', filters.modality));
      }
      if (filters.status) {
        constraints.push(where('status', '==', filters.status));
      }
      if (filters.sessionType) {
        constraints.push(where('sessionType', '==', filters.sessionType));
      }
      if (filters.startDate) {
        constraints.push(where('appointmentDate', '>=', Timestamp.fromDate(filters.startDate)));
      }
      if (filters.endDate) {
        constraints.push(where('appointmentDate', '<=', Timestamp.fromDate(filters.endDate)));
      }

      constraints.push(orderBy('appointmentDate', 'asc'));

      const q = query(collection(db, this.appointmentsCollection), ...constraints);

      return onSnapshot(q, snapshot => {
        let appointments = snapshot.docs.map(doc =>
          this.firestoreToAppointment({ id: doc.id, ...doc.data() })
        );

        if (!filters.includeCancelled) {
          appointments = appointments.filter(
            a => a.status !== 'cancelled' && a.status !== 'no-show'
          );
        }

        callback(appointments);
      }, (error) => {
        console.error('Appointment subscription error (likely permissions):', error);
        // Still call callback with empty array so UI doesn't hang
        callback([]);
      });
    } catch (error) {
      console.error('Error subscribing to appointments:', error);
      throw error;
    }
  }

  /**
   * Subscribe to a single appointment
   */
  subscribeToAppointment(id: string, callback: (appointment: Appointment | null) => void): Unsubscribe {
    try {
      const docRef = doc(db, this.appointmentsCollection, id);

      return onSnapshot(docRef, docSnap => {
        if (docSnap.exists()) {
          callback(this.firestoreToAppointment({ id: docSnap.id, ...docSnap.data() }));
        } else {
          callback(null);
        }
      });
    } catch (error) {
      console.error('Error subscribing to appointment:', error);
      throw error;
    }
  }

  // ==================== Helper Methods ====================

  private async generateSlots(
    providerId: string,
    date: Date,
    startTime: string,
    endTime: string,
    slotDuration: number,
    modalities: AppointmentModality[],
    breakTimes?: Array<{ startTime: string; endTime: string; reason?: string }>
  ): Promise<TimeSlot[]> {
    const slots: TimeSlot[] = [];
    const startMinutes = this.timeToMinutes(startTime);
    const endMinutes = this.timeToMinutes(endTime);
    // Use slot duration + 15 min buffer as the increment between appointments
    const slotIncrement = slotDuration + 15;

    for (let minutes = startMinutes; minutes < endMinutes; minutes += slotIncrement) {
      const slotStart = this.minutesToTime(minutes);
      const slotEnd = this.minutesToTime(minutes + slotDuration);

      // Check if slot is during break time
      const isDuringBreak = breakTimes?.some(breakTime => {
        const breakStart = this.timeToMinutes(breakTime.startTime);
        const breakEnd = this.timeToMinutes(breakTime.endTime);
        return minutes >= breakStart && minutes < breakEnd;
      });

      if (isDuringBreak) {
        continue;
      }

      // Check if slot is already booked
      const existingAppointment = await this.findAppointmentAtTime(providerId, date, slotStart);

      slots.push({
        startTime: slotStart,
        endTime: slotEnd,
        available: !existingAppointment,
        appointmentId: existingAppointment?.id,
        modalities
      });
    }

    return slots;
  }

  private async findAppointmentAtTime(
    providerId: string,
    date: Date,
    startTime: string
  ): Promise<Appointment | undefined> {
    try {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      const q = query(
        collection(db, this.appointmentsCollection),
        where('providerId', '==', providerId),
        where('appointmentDate', '>=', Timestamp.fromDate(startOfDay)),
        where('appointmentDate', '<=', Timestamp.fromDate(endOfDay)),
        where('startTime', '==', startTime)
      );

      const querySnapshot = await getDocs(q);

      for (const doc of querySnapshot.docs) {
        const appointment = this.firestoreToAppointment({ id: doc.id, ...doc.data() });
        if (appointment.status !== 'cancelled' && appointment.status !== 'no-show') {
          return appointment;
        }
      }

      return undefined;
    } catch (error) {
      console.error('Error finding appointment at time:', error);
      return undefined;
    }
  }

  private generateVideoLink(appointmentId: string): string {
    return `https://video.mycliniccore.com/session/${appointmentId}`;
  }

  private generateMeetingRoomId(): string {
    return `room_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getAppointmentDateTime(appointment: Appointment): Date {
    const d = new Date(appointment.appointmentDate);
    // Use UTC components to avoid timezone shift (Firestore stores dates as UTC midnight)
    const date = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const [hours, minutes] = appointment.startTime.split(':').map(Number);
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private minutesToTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  private firestoreToAppointment(data: any): Appointment {
    return {
      ...data,
      appointmentDate: data.appointmentDate?.toDate ? data.appointmentDate.toDate() : new Date(data.appointmentDate),
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt)
    };
  }

  // ==================== Admin/Testing Methods ====================

  /**
   * Clear all appointments (for testing/reset)
   */
  async clearAllAppointments(): Promise<void> {
    try {
      const querySnapshot = await getDocs(collection(db, this.appointmentsCollection));
      const batch = writeBatch(db);

      querySnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      console.log('All appointments cleared');
    } catch (error) {
      console.error('Error clearing appointments:', error);
      throw error;
    }
  }

  /**
   * Clear provider availability (for testing/reset)
   */
  async clearProviderAvailability(providerId?: string): Promise<void> {
    try {
      const constraints: QueryConstraint[] = [];

      if (providerId) {
        constraints.push(where('providerId', '==', providerId));
      }

      const q = query(collection(db, this.availabilityCollection), ...constraints);
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);

      querySnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      console.log(`Provider availability cleared${providerId ? ` for ${providerId}` : ''}`);
    } catch (error) {
      console.error('Error clearing provider availability:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const firebaseAppointmentService = FirebaseAppointmentService.getInstance();
export default FirebaseAppointmentService;
